import { useState, useEffect } from 'react';
import api from '../services/api';
import { trackEvent } from '../services/analytics';
import SubscriptionGate from '../components/SubscriptionGate';

/* ─── Guest Review Writer Modal ─── */
function GuestReviewModal({ onClose, propertyName }) {
  const [guestName, setGuestName] = useState('');
  const [stayDates, setStayDates] = useState('');
  const [cleanliness, setCleanliness] = useState(5);
  const [communication, setCommunication] = useState(5);
  const [houseRules, setHouseRules] = useState(5);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!guestName.trim()) return;
    setLoading(true);
    try {
      const data = await api.post('/guest-review', { guest_name: guestName.trim(), stay_dates: stayDates.trim(), cleanliness_rating: cleanliness, communication_rating: communication, house_rules_rating: houseRules, overall_notes: notes.trim() });
      setResult(data);
      trackEvent('guest_review_generated');
    } catch (err) { alert('Failed to generate review: ' + (err.message || 'Unknown error')); }
    finally { setLoading(false); }
  };

  const handleCopy = () => { navigator.clipboard.writeText(result.review); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const StarPicker = ({ value, onChange, label }) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', gap: 4 }}>{[1,2,3,4,5].map(i => <span key={i} onClick={() => onChange(i)} style={{ fontSize: 22, cursor: 'pointer', color: i <= value ? '#f59e0b' : '#ddd', transition: 'color 0.1s' }}>★</span>)}</div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content card" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header"><h2 className="modal-title">Write Guest Review</h2><button className="modal-close" onClick={onClose}>&times;</button></div>
        <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20 }}>Fill in the quick details and AI will write a personalised host-to-guest review.</p>
        {!result ? (
          <div>
            <div className="form-group"><label className="form-label">Guest Name</label><input className="form-input" type="text" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="e.g. Sarah" autoFocus /></div>
            <div className="form-group"><label className="form-label">Stay Dates</label><input className="form-input" type="text" value={stayDates} onChange={e => setStayDates(e.target.value)} placeholder="e.g. 15-18 March 2026" /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <StarPicker label="Cleanliness" value={cleanliness} onChange={setCleanliness} />
              <StarPicker label="Communication" value={communication} onChange={setCommunication} />
              <StarPicker label="House Rules" value={houseRules} onChange={setHouseRules} />
            </div>
            <div className="form-group"><label className="form-label">Quick Notes <span style={{ fontWeight: 400, color: 'var(--text-light)' }}>(optional)</span></label><textarea className="form-input" value={notes} rows={3} onChange={e => setNotes(e.target.value)} placeholder="e.g. Very tidy, communicated well" style={{ resize: 'vertical' }} /></div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleGenerate} disabled={loading || !guestName.trim()}>{loading ? 'Writing...' : 'Generate Review'}</button></div>
          </div>
        ) : (
          <div>
            <div style={{ padding: '16px 20px', background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)', borderRadius: 10, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Review of {guestName}</div>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text)', margin: 0 }}>{result.review}</p>
            </div>
            {result.private_note && <div style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--text-light)' }}><strong>Private note:</strong> {result.private_note}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}><button className="btn btn-ghost" onClick={() => setResult(null)}>Regenerate</button><button className="btn btn-primary" onClick={handleCopy}>{copied ? '✓ Copied!' : 'Copy Review'}</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Styles ─── */
function IntelligenceStyles() {
  return (
    <style>{`
      .intel-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
      .intel-section { padding: 24px; }
      .intel-label { font-size: 11px; font-weight: 700; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px; }
      .intel-pill { display: inline-block; padding: 4px 12px; background: var(--bg); border: 1px solid var(--border); border-radius: 16px; font-size: 12px; margin: 0 6px 6px 0; color: var(--text); }
      .intel-pill.highlight { background: rgba(52,211,153,0.08); border-color: rgba(52,211,153,0.2); color: #2ab380; }
      .intel-pill.warning { background: rgba(212,168,67,0.08); border-color: rgba(212,168,67,0.2); color: #b8941e; }
      .intel-title-suggestion { padding: 12px 16px; background: var(--bg); border-radius: 8px; margin-bottom: 8px; display: flex; align-items: center; justify-content: space-between; }
      .intel-title-text { font-size: 14px; font-weight: 600; color: var(--text); }
      .intel-copy-btn { background: none; border: 1px solid var(--border); border-radius: 6px; padding: 4px 10px; font-size: 11px; cursor: pointer; color: var(--text-light); font-family: inherit; }
      .intel-copy-btn:hover { border-color: var(--accent); color: var(--accent); }
      .intel-amenity-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--border); }
      .intel-amenity-row:last-child { border-bottom: none; }
      .intel-amenity-action { flex-shrink: 0; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
      .intel-pricing-card { padding: 20px 24px; border-radius: 12px; text-align: center; }
      .intel-generating { text-align: center; padding: 60px 24px; }
      .intel-generating-icon { font-size: 40px; margin-bottom: 16px; animation: pulse 2s ease-in-out infinite; }
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      @media (max-width: 768px) { .intel-grid { grid-template-columns: 1fr; } }
    `}</style>
  );
}

function SectionHeader({ icon, title, description }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="intel-label">{icon} {title}</div>
      {description && <p style={{ fontSize: 12, color: 'var(--text-light)', margin: '4px 0 0', lineHeight: 1.5 }}>{description}</p>}
    </div>
  );
}

const safeArray = (val) => Array.isArray(val) ? val : [];
const safeStr = (obj, ...keys) => {
  if (typeof obj === 'string') return obj;
  if (!obj || typeof obj !== 'object') return '';
  for (const k of keys) { if (obj[k] && typeof obj[k] === 'string') return obj[k]; }
  return Object.values(obj).find(v => typeof v === 'string' && v.length > 2 && v.length < 120) || '';
};
const safeNum = (obj, ...keys) => {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) { if (obj[k] !== undefined && obj[k] !== null) return Number(obj[k]); }
  return null;
};

/* ─── Main Page ─── */
export default function ListingIntelligencePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showGuestReview, setShowGuestReview] = useState(false);
  const [business, setBusiness] = useState(null);
  const [user, setUser] = useState(null);
  const [descCopied, setDescCopied] = useState(false);
  const [refreshCooldown, setRefreshCooldown] = useState(null);

  const isSubscribed = ['active', 'trialing'].includes(user?.subscription_status);

  const normalizeData = (result) => {
    if (result && result.status === 'ok') {
      result.title_suggestions = safeArray(result.title_suggestions);
      result.guest_language = safeArray(result.guest_language);
      result.strengths = safeArray(result.strengths);
      result.improvements = safeArray(result.improvements);
      result.amenity_actions = safeArray(result.amenity_actions);
      result.seasonal_insights = safeArray(result.seasonal_insights);
    }
    return result;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [businesses, me] = await Promise.all([api.getBusinesses(), api.getMe()]);
        if (businesses.length > 0) setBusiness(businesses[0]);
        setUser(me);
        const result = await api.get('/listing-intelligence');
        const normalized = normalizeData(result);
        setData(normalized);
        if (normalized?.status === 'insufficient_data') setError(normalized.message);
        trackEvent('listing_intelligence_viewed');
      } catch (err) {
        if (err.response?.status === 400) setError('Listing Intelligence is available for hospitality accounts only.');
        else setError(err.response?.data?.message || err.message);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleRefresh = async () => {
    if (refreshCooldown) return;
    setRefreshing(true); setError(null);
    try {
      const result = await api.post('/listing-intelligence/refresh', {});
      setData(normalizeData(result));
      trackEvent('listing_intelligence_refreshed');
      setRefreshCooldown(300);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Refresh failed';
      if (err.response?.status === 429) setRefreshCooldown(60);
      setError(detail);
      setTimeout(() => setError(null), 8000);
    }
    setRefreshing(false);
  };

  useEffect(() => {
    if (!refreshCooldown) return;
    const timer = setInterval(() => { setRefreshCooldown(prev => { if (prev <= 1) { clearInterval(timer); return null; } return prev - 1; }); }, 1000);
    return () => clearInterval(timer);
  }, [refreshCooldown]);

  const handleCopyDescription = () => { navigator.clipboard.writeText(data.optimised_description); setDescCopied(true); trackEvent('listing_description_copied'); setTimeout(() => setDescCopied(false), 2000); };
  const handleCopyTitle = (title) => { navigator.clipboard.writeText(title); trackEvent('listing_title_copied'); };

  const pricingColors = {
    increase: { bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.2)', color: '#2ab380', icon: '📈' },
    maintain: { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.2)', color: '#3b82f6', icon: '📊' },
    decrease: { bg: 'rgba(212,168,67,0.08)', border: 'rgba(212,168,67,0.2)', color: '#b8941e', icon: '📉' },
  };

  const actionColors = {
    highlight: { bg: 'rgba(52,211,153,0.08)', color: '#2ab380', label: 'Highlight' },
    add: { bg: 'rgba(59,130,246,0.08)', color: '#3b82f6', label: 'Add' },
    fix: { bg: 'rgba(220,38,38,0.08)', color: '#dc2626', label: 'Fix' },
  };

  return (
    <div>
      <IntelligenceStyles />

      <div className="page-header">
        <div>
          <h1 className="page-title">Listing Intelligence</h1>
          <p className="page-subtitle">AI-powered insights to optimise your listing and get more bookings</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {data?.status === 'ok' && (
            <SubscriptionGate isSubscribed={isSubscribed} feature="Refreshing analysis">
              <button className="btn btn-ghost" onClick={handleRefresh} disabled={refreshing || !!refreshCooldown}>
                {refreshing ? 'Analysing...' : refreshCooldown ? `Wait ${Math.ceil(refreshCooldown / 60)}m` : '🔄 Refresh Analysis'}
              </button>
            </SubscriptionGate>
          )}
          <SubscriptionGate isSubscribed={isSubscribed} feature="Writing guest reviews">
            <button className="btn btn-primary" onClick={() => setShowGuestReview(true)}>✍️ Write Guest Review</button>
          </SubscriptionGate>
        </div>
      </div>

      {(loading || refreshing) && (
        <div className="card intel-generating">
          <div className="intel-generating-icon">🧠</div>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px', color: 'var(--text)' }}>{refreshing ? 'Refreshing your listing intelligence...' : 'Generating listing intelligence...'}</h3>
          <p style={{ fontSize: 13, color: 'var(--text-light)', margin: '0 0 16px', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>AI is analysing your guest reviews to create optimised descriptions, title suggestions, amenity recommendations, and pricing insights.</p>
          <p style={{ fontSize: 12, color: 'var(--text-light)' }}>This usually takes 10-15 seconds...</p>
        </div>
      )}

      {error && !data && !loading && !refreshing && (
        <div className="card" style={{ padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🏠</div>
          <p style={{ fontSize: 14, color: 'var(--text-light)', maxWidth: 400, margin: '0 auto', lineHeight: 1.6 }}>{error}</p>
          {data?.status === 'insufficient_data' && <p style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 12 }}><a href="/reviews" style={{ color: 'var(--accent)', fontWeight: 600 }}>Add more reviews</a> to unlock listing intelligence.</p>}
        </div>
      )}

      {data && data.status === 'ok' && !loading && !refreshing && (
        <>
          {error && data && <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 8, background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.15)', fontSize: 13, color: '#dc2626' }}>{error}</div>}

          {data.cached && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', marginBottom: 16, borderRadius: 8, background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.1)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-light)' }}>Last analysed: {data.cached_at ? new Date(data.cached_at).toLocaleString() : 'recently'} · {data.review_count} reviews analysed</span>
              <SubscriptionGate isSubscribed={isSubscribed} feature="Refreshing analysis">
                <button className="btn btn-ghost btn-sm" onClick={handleRefresh} disabled={refreshing || !!refreshCooldown} style={{ fontSize: 11 }}>{refreshing ? 'Refreshing...' : refreshCooldown ? `Wait ${Math.ceil(refreshCooldown / 60)}m` : 'Refresh'}</button>
              </SubscriptionGate>
            </div>
          )}

          {data.summary && (
            <div className="card" style={{ padding: '16px 20px', marginBottom: 24, borderLeft: '3px solid var(--accent)' }}>
              <p style={{ fontSize: 14, color: 'var(--text)', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>{data.summary}</p>
              <span style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 8, display: 'block' }}>Based on {data.review_count} guest review{data.review_count !== 1 ? 's' : ''}</span>
            </div>
          )}

          {data.optimised_description && (
            <div className="card intel-section" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div className="intel-label">✏️ Optimised Listing Description</div>
                  <span style={{ fontSize: 12, color: 'var(--text-light)', lineHeight: 1.5 }}>Copy this and paste it into your Airbnb listing description. It uses the exact words your guests use to describe your property.</span>
                </div>
                <button className="btn btn-primary" onClick={handleCopyDescription} style={{ flexShrink: 0 }}>{descCopied ? '✓ Copied!' : '📋 Copy to Clipboard'}</button>
              </div>
              <div style={{ padding: '20px 24px', background: 'var(--bg)', borderRadius: 10, fontSize: 14, lineHeight: 1.8, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{data.optimised_description}</div>
            </div>
          )}

          <div className="intel-grid">
            {data.title_suggestions?.length > 0 && (
              <div className="card intel-section">
                <SectionHeader icon="🏷️" title="Title Suggestions" description="Replace your current Airbnb listing title with one of these. Based on the strongest themes from your reviews." />
                {data.title_suggestions.map((title, i) => { const text = safeStr(title, 'title', 'suggestion', 'text', 'name'); return <div key={i} className="intel-title-suggestion"><span className="intel-title-text">{text}</span><button className="intel-copy-btn" onClick={() => handleCopyTitle(text)}>Copy</button></div>; })}
              </div>
            )}
            {data.guest_language?.length > 0 && (
              <div className="card intel-section">
                <SectionHeader icon="🗣️" title="Words Your Guests Use" description="Include these words in your listing title and description. They match what real travellers search for on Airbnb." />
                <div>{data.guest_language.map((word, i) => { const text = safeStr(word, 'word', 'phrase', 'name', 'term'); return <span key={i} className="intel-pill highlight">{text}</span>; })}</div>
              </div>
            )}
          </div>

          <div className="intel-grid">
            {data.strengths?.length > 0 && (
              <div className="card intel-section">
                <SectionHeader icon="💪" title="What Guests Love" description="These are your strongest selling points. Make sure they're prominently mentioned in your listing." />
                {data.strengths.map((s, i) => { const label = safeStr(s, 'theme', 'strength', 'name', 'title', 'area'); const count = safeNum(s, 'mentions', 'mention_count', 'count'); const quote = safeStr(s, 'guest_quote', 'evidence', 'quote'); return <div key={i} style={{ marginBottom: 14 }}><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</span>{count && <span className="intel-pill highlight" style={{ margin: 0, padding: '2px 8px' }}>{count}x</span>}</div>{quote && <p style={{ fontSize: 12, color: 'var(--text-light)', margin: 0, fontStyle: 'italic' }}>"{quote}"</p>}</div>; })}
              </div>
            )}
            {data.improvements?.length > 0 && (
              <div className="card intel-section">
                <SectionHeader icon="💡" title="Areas to Improve" description="Address these to boost your rating. Each suggestion comes from real guest feedback." />
                {data.improvements.map((imp, i) => { const label = safeStr(imp, 'theme', 'improvement', 'area', 'name', 'title'); const count = safeNum(imp, 'mentions', 'mention_count', 'count'); const suggestion = safeStr(imp, 'suggestion', 'evidence', 'description', 'recommendation'); return <div key={i} style={{ marginBottom: 14 }}><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</span>{count && <span className="intel-pill warning" style={{ margin: 0, padding: '2px 8px' }}>{count}x</span>}</div>{suggestion && <p style={{ fontSize: 12, color: 'var(--text-light)', margin: 0 }}>→ {suggestion}</p>}</div>; })}
              </div>
            )}
          </div>

          <div className="intel-grid">
            {data.amenity_actions?.length > 0 && (
              <div className="card intel-section">
                <SectionHeader icon="🏡" title="Amenity Action List" description="Your to-do list: Highlight = mention more in your listing. Add = guests want this. Fix = guests complained about this." />
                {data.amenity_actions.map((a, i) => { const action = safeStr(a, 'action', 'type', 'category').toLowerCase() || 'highlight'; const amenity = safeStr(a, 'amenity', 'name', 'item', 'title', 'feature'); const reason = safeStr(a, 'reason', 'description', 'detail', 'rationale', 'explanation'); if (!amenity) return null; const s = actionColors[action] || actionColors.highlight; return <div key={i} className="intel-amenity-row"><span className="intel-amenity-action" style={{ background: s.bg, color: s.color }}>{s.label}</span><div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{amenity}</div>{reason && <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{reason}</div>}</div></div>; })}
              </div>
            )}
            {data.pricing_signal && typeof data.pricing_signal === 'object' && (
              <div className="card intel-section">
                <SectionHeader icon="💰" title="Pricing Signal" description="Based on guest satisfaction and competitor comparison. A guide, not a directive." />
                {(() => { const ps = data.pricing_signal; const direction = safeStr(ps, 'direction', 'recommendation') || 'maintain'; const s = pricingColors[direction] || pricingColors.maintain; const confidence = safeStr(ps, 'confidence', 'level') || 'medium'; const reasoning = safeStr(ps, 'reasoning', 'explanation', 'description'); return <div className="intel-pricing-card" style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 12 }}><div style={{ fontSize: 32, marginBottom: 8 }}>{s.icon}</div><div style={{ fontSize: 18, fontWeight: 700, color: s.color, marginBottom: 4, textTransform: 'capitalize' }}>{direction} Price</div><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-light)', marginBottom: 12 }}>Confidence: {confidence}</div><p style={{ fontSize: 13, color: 'var(--text)', margin: 0, lineHeight: 1.6 }}>{reasoning}</p></div>; })()}
                {data.seasonal_insights?.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <SectionHeader icon="📅" title="Seasonal Patterns" description="Adjust pricing or listing highlights based on these seasonal trends." />
                    {data.seasonal_insights.map((si, i) => { const season = safeStr(si, 'season', 'period', 'name', 'time'); const insight = safeStr(si, 'insight', 'description', 'detail', 'note', 'observation'); return <div key={i} style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start' }}>{season && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', flexShrink: 0 }}>{season}:</span>}<span style={{ fontSize: 13, color: 'var(--text-light)' }}>{insight || season}</span></div>; })}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {showGuestReview && <GuestReviewModal onClose={() => setShowGuestReview(false)} propertyName={business?.name || ''} />}
    </div>
  );
}
